import { initClient, tsRestFetchApi } from '@ts-rest/core';
import { CLIENT_HEADER, WEB_CLIENT, contract } from '@logistics/contracts';

/**
 * Empty in development, where Vite proxies /api to the API on the same origin.
 * In production the API must be on the same site as the panel, so the session
 * cookie travels with each request (docs/web-auth.md).
 */
export const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();

/**
 * Called when a request is answered 401: the session has expired or was
 * revoked. The app listens and re-reads who is signed in, which brings up the
 * sign-in screen. Raw `fetch` callers (the chat stream) call it themselves.
 */
export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

export function notifyUnauthorized(): void {
  for (const listener of unauthorizedListeners) listener();
}

/** `GET /auth/me` answers 401 when signed out; that is its answer, not an expiry. */
const ME_PATH = contract.auth.me.path;

/**
 * One typed client for the whole panel, generated from the contract: a route, a
 * query parameter or a response field that does not exist is a type error here
 * rather than a 404 in the browser.
 */
export const api = initClient(contract, {
  baseUrl: BASE_URL,
  // Sends the httpOnly session cookie; JavaScript never sees it.
  credentials: 'include',
  baseHeaders: {
    // The CSRF check: a cookie-authenticated change must carry this header.
    [CLIENT_HEADER]: WEB_CLIENT,
  },
  api: async (args) => {
    const result = await tsRestFetchApi(args);
    if (result.status === 401 && args.route.path !== ME_PATH) notifyUnauthorized();
    return result;
  },
});
